import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { PALETAS } from '@shared/config/paleta';

const STORAGE_KEY = 'sgi_paleta_seleccionada';

type CssVariables = Record<string, string>;
type PaletteKey = keyof typeof PALETAS.palettes;
type PaletteColors = (typeof PALETAS.palettes)[PaletteKey]['colors'];

@Injectable({ providedIn: 'root' })
export class PaletaService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  /** Paleta activa, por defecto "blue" */
  readonly paletaSeleccionada = signal<string>(
    this.cargarDeStorage() ?? 'blue'
  );

  /** Opciones para el <p-select> */
  readonly opcionesPaleta = Object.entries(PALETAS.palettes).map(([key, p]) => ({
    label: p.name,
    value: key
  }));

  /**
   * Variables que gestiona el tema público de congresos.
   * Sirven para limpiarlas al cambiar de congreso o volver al modo admin.
   */
  readonly congresoThemeVars: string[] = [
    '--congreso-primary',
    '--congreso-secondary',
    '--congreso-text',
    '--congreso-background',
    '--congreso-surface',
    '--congreso-muted-text',
    '--congreso-accent',

    '--p-primary-color',
    '--p-primary-contrast-color',
    '--p-primary-hover-color',
    '--p-primary-active-color',
    '--p-primary-50',
    '--p-primary-100',
    '--p-primary-200',
    '--p-primary-300',
    '--p-primary-400',
    '--p-primary-500',
    '--p-primary-600',
    '--p-primary-700',
    '--p-primary-800',
    '--p-primary-900',
    '--p-primary-950'
  ];

  constructor() {
    this.aplicarCSS();
  }

  // ─────────────────────────────
  // Setter
  // ─────────────────────────────

  setPaleta(key: string): void {
    if (!this.existePaleta(key)) return;

    this.paletaSeleccionada.set(key);
    this.aplicarCSS();
    this.guardarEnStorage();
  }

  reset(): void {
    this.paletaSeleccionada.set('blue');
    this.aplicarCSS();
    this.guardarEnStorage();
  }

  // ─────────────────────────────
  // CSS variables generales de la app
  // ─────────────────────────────

  /**
   * Vuelca todos los colores de la paleta activa + neutros + semánticos
   * en :root como variables CSS:
   *
   * --app-primary900
   * --app-neutralBackground
   * --app-success
   */
  aplicarCSS(): void {
    if (!this.isBrowser()) return;

    const root = this.document.documentElement;
    const paleta = this.getPaletaColors(this.paletaSeleccionada());

    if (!paleta) return;

    // Colores de la paleta activa
    Object.entries(paleta).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(`--app-${key}`, value);
      }
    });

    // Neutros
    Object.entries(PALETAS.neutral).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(
          `--app-neutral${key.charAt(0).toUpperCase() + key.slice(1)}`,
          value
        );
      }
    });

    // Semánticos
    Object.entries(PALETAS.semantic).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(`--app-${key}`, value);
      }
    });
  }

  // ─────────────────────────────
  // Lectura centralizada de paletas
  // ─────────────────────────────

  getPaletaColors(key: string | null | undefined): PaletteColors | null {
    if (!key || !this.isPaletteKey(key)) return null;

    return PALETAS.palettes[key].colors;
  }

  existePaleta(key: string): boolean {
    return this.isPaletteKey(key);
  }

  /**
   * Variables propias de las páginas públicas de congreso.
   *
   * CongresoThemeService debe usar este método en vez de leer PALETAS directamente.
   */
  getCongresoCssVariables(paletteKey: string | null | undefined): CssVariables {
    const paleta = this.getPaletaColors(paletteKey);
    const neutral = PALETAS.neutral;

    if (!paleta) return {};

    return {
      '--congreso-primary': this.getColor(paleta, 'primary800', '#1d4ed8'),
      '--congreso-secondary': this.getColor(paleta, 'primary600', '#2563eb'),
      '--congreso-text': neutral.text,
      '--congreso-background': neutral.background,
      '--congreso-surface': neutral.surface,
      '--congreso-muted-text': neutral.muted,
      '--congreso-accent': this.getColor(paleta, 'primary500', '#3b82f6')
    };
  }

  /**
   * Variables primary de PrimeNG.
   * Botones, focus, highlights y componentes PrimeNG heredan la paleta del congreso.
   */
  getPrimePrimaryCssVariables(paletteKey: string | null | undefined): CssVariables {
    const paleta = this.getPaletaColors(paletteKey);

    if (!paleta) return {};

    return {
      '--p-primary-color': this.getColor(paleta, 'primary800', '#1d4ed8'),
      '--p-primary-contrast-color': this.getColor(paleta, 'primaryContrast', '#ffffff'),
      '--p-primary-hover-color': this.getColor(paleta, 'primary900', '#1e3a8a'),
      '--p-primary-active-color': this.getColor(paleta, 'primary950', '#172554'),

      '--p-primary-50': this.getColor(paleta, 'primary50', '#eff6ff'),
      '--p-primary-100': this.getColor(paleta, 'primary100', '#dbeafe'),
      '--p-primary-200': this.getColor(paleta, 'primary200', '#bfdbfe'),
      '--p-primary-300': this.getColor(paleta, 'primary300', '#93c5fd'),
      '--p-primary-400': this.getColor(paleta, 'primary400', '#60a5fa'),
      '--p-primary-500': this.getColor(paleta, 'primary500', '#3b82f6'),
      '--p-primary-600': this.getColor(paleta, 'primary600', '#2563eb'),
      '--p-primary-700': this.getColor(paleta, 'primary700', '#1d4ed8'),
      '--p-primary-800': this.getColor(paleta, 'primary800', '#1e40af'),
      '--p-primary-900': this.getColor(paleta, 'primary900', '#1e3a8a'),
      '--p-primary-950': this.getColor(paleta, 'primary950', '#172554')
    };
  }

  private isPaletteKey(key: string): key is PaletteKey {
    return Object.prototype.hasOwnProperty.call(PALETAS.palettes, key);
  }

  private getColor(colors: PaletteColors, key: string, fallback: string): string {
    const value = (colors as unknown as Record<string, string | undefined>)[key];

    return value ?? fallback;
  }

  // ─────────────────────────────
  // Persistencia
  // ─────────────────────────────

  private cargarDeStorage(): string | null {
    if (!this.isBrowser()) return null;

    return localStorage.getItem(STORAGE_KEY);
  }

  private guardarEnStorage(): void {
    if (!this.isBrowser()) return;

    localStorage.setItem(STORAGE_KEY, this.paletaSeleccionada());
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}